import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import { SubledgerView } from '../../../models/ledger/subledger';
import { SubledgerType } from '../../../models/financial-account/financial-account';
import { FinancialAccountProxyService } from '../../../proxies/financial-account.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';

/**
 * Level 2 — a reconciliation account broken down by subledger party as of a date. Generic across subledger
 * types (provider / client / bank / internal company); the party total reconciles to the GL account balance.
 */
@Component({
  selector: 'app-account-subledger',
  templateUrl: './account-subledger.component.html',
  styleUrl: './account-subledger.component.scss',
})
export class AccountSubledgerComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly proxy = inject(FinancialAccountProxyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly isLoading = signal(0);
  readonly view = signal<SubledgerView | null>(null);
  // Period-to-date view (#4713): pick a fiscal month; the subledger shows everything through that month's
  // last day — so an in-month posting dated later than today (e.g. the 31st) is included and ties to the GL
  // balance — instead of the old "as of today" cut-off that hid it.
  readonly year = signal<number>(new Date().getFullYear());
  readonly period = signal<number>(new Date().getMonth() + 1);

  readonly monthOptions = Array.from({ length: 12 }, (_, i) => ({
    label: new Date(2000, i, 1).toLocaleDateString(undefined, { month: 'long' }),
    value: i + 1,
  }));
  readonly yearOptions = (() => {
    const current = new Date().getFullYear();
    return [current - 3, current - 2, current - 1, current, current + 1].map((v) => ({ label: String(v), value: v }));
  })();

  /** false = parties with an outstanding balance only (default); true = also list settled (zero) parties. */
  readonly includeSettled = signal(false);

  readonly scopeOptions = [
    { label: this.t('COA_Outstanding', 'Outstanding'), value: false },
    { label: this.t('COA_AllItems', 'All'), value: true },
  ];

  private accountId: string | null = null;

  readonly accountName = computed(() => {
    const v = this.view();
    return v ? this.locale.getLocalizedValue(v.accountName) : '';
  });

  private readonly syncBreadcrumb = effect(() => this.breadcrumbService.setCurrent(this.accountName()), { allowSignalWrites: true });

  /** Singular noun for the party type (e.g. "provider"), used in the subtitle + summary. */
  readonly partyNoun = computed(() => this.nounFor(this.view()?.subledgerType, false));
  readonly partyNounPlural = computed(() => this.nounFor(this.view()?.subledgerType, true));

  ngOnInit(): void {
    this.accountId = this.route.snapshot.queryParamMap.get('accountId');
    if (!this.accountId) {
      this.back();
      return;
    }
    this.includeSettled.set(this.route.snapshot.queryParamMap.get('includeSettled') === 'true');
    const qp = this.route.snapshot.queryParamMap;
    const y = Number(qp.get('fiscalYear'));
    const p = Number(qp.get('period'));
    if (Number.isInteger(y) && y > 0) this.year.set(y);
    if (Number.isInteger(p) && p >= 1 && p <= 12) this.period.set(p);
    this.load();
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private load(): void {
    if (!this.accountId) return;
    this.isLoading.update((v) => v + 1);
    this.proxy
      .getSubledger(this.accountId, this.year(), this.period(), this.includeSettled())
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => this.view.set(response.data ?? null),
        error: () => this.view.set(null),
      });
  }

  onPeriodChange(period: number): void {
    this.period.set(period);
    this.load();
  }

  onYearChange(year: number): void {
    this.year.set(year);
    this.load();
  }

  onScopeChange(includeSettled: boolean): void {
    this.includeSettled.set(includeSettled);
    this.load();
  }

  private nounFor(type: SubledgerType | undefined, plural: boolean): string {
    switch (type) {
      case SubledgerType.Provider:
        return plural ? this.t('COA_Providers', 'providers') : this.t('COA_Provider', 'provider');
      case SubledgerType.Client:
        return plural ? this.t('COA_Clients', 'clients') : this.t('COA_Client', 'client');
      case SubledgerType.Bank:
        return plural ? this.t('COA_Banks', 'banks') : this.t('COA_Bank', 'bank');
      case SubledgerType.InternalCompany:
        return plural ? this.t('COA_Companies', 'companies') : this.t('COA_Company', 'company');
      case SubledgerType.Employee:
        return plural ? this.t('COA_Employees', 'employees') : this.t('COA_Employee', 'employee');
      case SubledgerType.PettyCashFund:
        return plural ? this.t('COA_Funds', 'funds') : this.t('COA_Fund', 'fund');
      default:
        return plural ? this.t('COA_Parties', 'parties') : this.t('COA_Party', 'party');
    }
  }

  openParty(partyReference: string): void {
    if (this.accountId) {
      // Carry the settled scope so a fully-paid party opens its statement already showing settled docs.
      this.router.navigate(['/chart-of-accounts/party'], {
        queryParams: {
          accountId: this.accountId,
          partyId: partyReference,
          fiscalYear: this.year(),
          period: this.period(),
          includeSettled: this.includeSettled() || undefined,
        },
      });
    }
  }

  back(): void {
    this.router.navigate(['/chart-of-accounts']);
  }

  export(): void {
    const v = this.view();
    if (!v) return;
    const header = ['Code', 'Party', 'Open', `Balance (${v.currency})`];
    const rows = v.parties.map((p) => [p.code ?? '', p.name ?? '', String(p.openCount), p.balance.toFixed(2)]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${v.accountNumber}-subledger.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
